import { useRouteLoaderData } from '@remix-run/react';
import type { Recommendation } from '@shared/dream-components';
import { type ReactNode, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import useEmail from '~/hooks/useEmail';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import useFormSubmit from './_hooks/useFormSubmit';

import { usePageContext } from '~/context/usePageContext';
import type { loader } from '~/root';

type RenderProps = {
  isLoading: boolean;
  email?: string;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  recommendations: Recommendation[];
  onSubmitSuccess?: (recommendations: Recommendation[]) => void;
};

const RecommendationsModalForm = ({ recommendations, children, onSubmitSuccess }: Props) => {
  const { t } = useTranslation();
  const [email] = useEmail();
  const fetcher = useComponentFetcher();
  const { publication } = useRouteLoaderData<typeof loader>('root');
  const { visitToken, referralId, setCookie } = usePageContext();

  const formRef = useRef<HTMLFormElement>(null);

  const { handleSubmit } = useFormSubmit({ fetcher });

  const handleSuccess = () => {
    setCookie({ name: 'email_capture_disabled', value: 'true', days: 30 });

    toast.success(t('notifications.subscribe.subscribed'));
    onSubmitSuccess?.(recommendations);
    formRef.current?.reset();
  };

  const handleError = (errPayload: any) => {
    toast.success(errPayload?.response?.data?.error || t('errors.default'));
  };

  const isLoading = fetcher.state === 'submitting';

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/recommendations_subscribe_form"
      onSubmit={(e) => {
        const formData = new FormData(e.currentTarget);
        handleSubmit(formData);
      }}
      onSuccess={handleSuccess}
      onError={handleError}
    >
      <StatusInputs name="recommendations" errorMessage={t('errors.default')} successMessage="" />
      {referralId && <input hidden name="ref" defaultValue={referralId} />}
      {visitToken && <input hidden name="visit_token" defaultValue={visitToken} />}
      <input hidden name="publication_id" defaultValue={publication.id} />
      {email && <input hidden name="email" defaultValue={email} />}
      <input name="sent_from_orchid" type="hidden" defaultValue="true" />
      <input name="include_current_publication" type="hidden" defaultValue="false" />
      {recommendations.map((rec) => (
        <input hidden key={rec.id} name="recommendation_ids[]" defaultValue={rec.id} />
      ))}
      {children({
        isLoading,
        email,
      })}
    </ActionForm>
  );
};

export default RecommendationsModalForm;
